import { createSlice } from '@reduxjs/toolkit';
import {
    getAllProjects,
    getProjectDetails,
    // getActivityStat,
    getProjectData,
    getProjectBudgetReview,
    SLICE_NAME
} from './service';
import { updateProject } from '../onboarding/service';
import { IProject, IProjectData, IProjectsList } from 'src/types/projects';
import { IWorkDuration } from 'src/types/workDuration';
import { IBudget } from 'src/types/budget';
import { WORK_DURATION } from 'src/constants/workDuration';

interface initialTypes {
    projects: IProject[];
    isLoading: boolean;
    data: { [id: string]: IProjectData };
    isDataLoading: boolean;
    error: string;
    workDuration: IWorkDuration;
    projectBudget: IBudget;
    projectsList: IProjectsList[];
}

const initialState: initialTypes = {
    projects: [],
    data: {},
    isLoading: false,
    isDataLoading: false,
    error: '',
    workDuration: WORK_DURATION,
    projectBudget: {},
    projectsList: []
};

export const ProjectsSlice = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {},

    extraReducers: {
        [getAllProjects.pending.type]: (state) => {
            state.isLoading = true;
        },
        [getAllProjects.fulfilled.type]: (state, { payload }) => {
            state.isLoading = false;
            state.projects = payload.projects;
        },
        [getAllProjects.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        },
        [getProjectDetails.pending.type]: (state) => {
            state.isLoading = true;
        },
        [getProjectDetails.fulfilled.type]: (state, action) => {
            state.isLoading = false;
            const projectId = action.meta.arg;
            const payload = action.payload;

            const existingIndex = state.projects.findIndex(
                (p) => p.id === projectId
            );

            if (existingIndex !== -1) {
                // Merge the details with existing project (keep id and projectName)
                state.projects[existingIndex] = {
                    ...state.projects[existingIndex],
                    ...payload
                };
            } else {
                // If project not found, add it
                state.projects.push({
                    id: projectId,
                    projectName: '',
                    ...payload
                } as IProject);
            }
        },
        [getProjectDetails.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        },
        [getProjectData.pending.type]: (state) => {
            state.isDataLoading = true;
        },
        [getProjectData.fulfilled.type]: (state, { payload }) => {
            state.isDataLoading = false;
            state.data = { ...state.data, [payload.id]: payload };
        },
        [getProjectData.rejected.type]: (state, action) => {
            state.isDataLoading = false;
            state.error = action.payload;
        },
        [getProjectBudgetReview.fulfilled.type]: (state, { payload }) => {
            const existing = state.data[payload.id];
            if (existing) {
                existing.budgetReviews = payload.budgetReviews;
            }
        },
        // Listen to updateProject from onboarding slice and update projects list
        [updateProject.fulfilled.type]: (state, action) => {
            const { projectId, priority, description } = action.meta.arg;
            const project = state.projects.find(
                (p) => p.id === projectId.toString()
            );

            if (project) {
                // Update only the fields that were provided
                if (priority !== undefined) {
                    project.priority = priority;
                }
                if (description !== undefined) {
                    project.description = description;
                }
                // Note: attachmentIds are sent to API but we need to refetch to get full attachment objects
                // The API should return the updated project with full attachment details
                if (
                    action.payload &&
                    typeof action.payload === 'object' &&
                    'attachments' in action.payload
                ) {
                    project.attachments = (
                        action.payload as { attachments: any[] }
                    ).attachments;
                }
            }
        }
    }
});

export const {} = ProjectsSlice.actions;

export default ProjectsSlice.reducer;
